import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ARTICLES_20 } from "@/lib/libraryArticles";
import { setPageMeta } from "@/lib/pageMeta";
import {
  ArrowLeft,
  Search,
  Sparkles,
  Heart,
  Brain,
  Utensils,
  Moon,
  Droplet,
  Baby,
  User as UserIcon,
  Users,
  Activity,
  Wind,
  Bone,
  Eye,
  Smile,
  Shield,
  Flame,
  ChevronRight,
} from "lucide-react";

// Curated, evidence-grounded educational content. Each article is short,
// non-diagnostic, and reflects widely accepted health guidance.

const CATEGORIES = [
  { key: "all", label: "All", Icon: Sparkles },
  { key: "cardio", label: "Heart", Icon: Heart },
  { key: "mental", label: "Mental", Icon: Brain },
  { key: "nutrition", label: "Nutrition", Icon: Utensils },
  { key: "sleep", label: "Sleep", Icon: Moon },
  { key: "hydration", label: "Hydration", Icon: Droplet },
  { key: "women", label: "Women", Icon: UserIcon },
  { key: "men", label: "Men", Icon: UserIcon },
  { key: "child", label: "Child", Icon: Baby },
  { key: "senior", label: "Senior", Icon: Users },
  { key: "diabetes", label: "Diabetes", Icon: Flame },
  { key: "immunity", label: "Immunity", Icon: Shield },
  { key: "bone", label: "Bone", Icon: Bone },
  { key: "eye", label: "Eye", Icon: Eye },
  { key: "skin", label: "Skin", Icon: Smile },
  { key: "lung", label: "Lung", Icon: Wind },
  { key: "activity", label: "Activity", Icon: Activity },
];

export const ARTICLES = [
  {
    id: "bp-basics",
    title: "Blood pressure — what the two numbers actually mean",
    category: "cardio",
    reading: "3 min",
    tags: ["hypertension", "heart", "systolic", "diastolic"],
    hero: "linear-gradient(135deg,#fb7185 0%,#f87171 100%)",
    excerpt:
      "Systolic pressure is when the heart pumps; diastolic is when it rests. Both matter — a healthy adult typically sits around 120/80.",
    body: [
      "Every time your heart beats, it pushes blood into the arteries. That push creates two numbers: systolic (top) is the peak pressure during a beat, diastolic (bottom) is the resting pressure between beats.",
      "A healthy adult range is commonly 120/80 mmHg. Consistently over 140/90 is generally considered high, and often improves with lifestyle changes: less salt (India runs high — try to cut pickles, papad, and packaged snacks), more potassium (banana, coconut water, tomato), regular walking, better sleep, and stress management.",
      "Low readings (below 90/60) with dizziness or fainting should be checked by a clinician.",
    ],
    key_points: [
      "120/80 is a common healthy target.",
      "Salt intake in Indian diets is often 2–3x the WHO recommendation of 5 g/day.",
      "30 minutes of brisk walking, 5 days/week has clinically meaningful effects.",
    ],
  },
  {
    id: "diabetes-101",
    title: "Type 2 diabetes — the story of insulin resistance",
    category: "diabetes",
    reading: "4 min",
    tags: ["hba1c", "insulin", "sugar", "glucose"],
    hero: "linear-gradient(135deg,#fbbf24 0%,#f59e0b 100%)",
    excerpt:
      "In Type 2 diabetes, your body's cells stop responding well to insulin. It doesn't happen overnight — it builds silently for years.",
    body: [
      "Insulin is the hormone that lets your cells absorb sugar from the blood. In insulin resistance, cells stop responding well — the pancreas has to work harder and harder, until it can't keep up. That's when blood sugar rises.",
      "The HbA1c test measures average blood sugar over ~3 months. Under 5.7% is normal; 5.7–6.4% is pre-diabetes; 6.5% or more is often considered diabetic range.",
      "Prevention & control is largely lifestyle: replace polished rice with millets or brown rice, walk 20 minutes after each meal, prioritize sleep, and lose 5–7% of body weight if overweight — that alone can dramatically reduce risk.",
    ],
    key_points: [
      "Millets (ragi, jowar, bajra) have a lower glycemic load than polished rice.",
      "A 20-minute post-meal walk lowers post-meal glucose meaningfully.",
      "Track HbA1c annually if you have a family history of diabetes.",
    ],
  },
  {
    id: "sleep-arch",
    title: "Sleep architecture — why 6h isn't enough",
    category: "sleep",
    reading: "3 min",
    tags: ["sleep", "rem", "deep sleep", "recovery"],
    hero: "linear-gradient(135deg,#a78bfa 0%,#7c3aed 100%)",
    excerpt:
      "Sleep isn't one thing — it's four stages cycled every 90 minutes. Cut it short and you lose the good stuff first.",
    body: [
      "Each sleep cycle is roughly 90 minutes and contains light sleep, deep sleep and REM sleep. Deep sleep does most of the physical recovery. REM is where memory and mood consolidate.",
      "REM concentrates in the second half of the night — so cutting sleep from 7–8 hours to 5–6 hours doesn't just lose 25% of sleep, it loses ~60% of your REM. That's why short-sleepers often feel emotionally 'off'.",
      "Simple wins: fix a wake-up time (matters more than bedtime), 30 minutes of morning sunlight, no caffeine after 2 pm, and a slightly cool bedroom (18–22°C).",
    ],
    key_points: [
      "Wake-up time consistency > bedtime consistency.",
      "Sunlight in the first hour tunes your body clock better than caffeine.",
      "Alcohol wrecks REM sleep even at 'small' doses.",
    ],
  },
  {
    id: "hydration-truth",
    title: "Hydration — the 8-glass rule was never a rule",
    category: "hydration",
    reading: "2 min",
    tags: ["water", "electrolytes", "dehydration"],
    hero: "linear-gradient(135deg,#60a5fa 0%,#2563eb 100%)",
    excerpt:
      "Your water need depends on your body, climate and activity. Urine colour is a better guide than a fixed glass count.",
    body: [
      "A common estimate is 30–35 ml of water per kg of body weight per day, adjusted upwards for hot climates and exercise. For a 65 kg adult that's ~2–2.3 L.",
      "Pale straw-coloured urine is a decent daily indicator you're hydrated. Dark yellow means you're behind; frequent clear/watery means you may be over-hydrating.",
      "In Indian summers, add a pinch of salt + lemon + a spoon of jaggery to your water once a day to keep electrolytes balanced — especially if you sweat heavily.",
    ],
    key_points: [
      "30 ml per kg body weight is a reasonable daily target.",
      "Urine colour beats counting glasses.",
      "Electrolytes matter when you sweat heavily.",
    ],
  },
  {
    id: "cholesterol",
    title: "Cholesterol — the good, the bad and the misunderstood",
    category: "cardio",
    reading: "3 min",
    tags: ["ldl", "hdl", "triglycerides"],
    hero: "linear-gradient(135deg,#f87171 0%,#dc2626 100%)",
    excerpt:
      "Cholesterol isn't evil — your body needs it. But LDL clogs arteries when it's too high, and HDL clears it out.",
    body: [
      "LDL is often called 'bad' cholesterol — high levels can stick to artery walls. HDL is 'good' cholesterol — it carries excess cholesterol back to the liver.",
      "Trans fats (in some biscuits, deep-fried street foods and margarine) are the worst offenders. Saturated fats (ghee, coconut oil, red meat) in moderation are widely considered fine.",
      "The heart-friendly wins: more soluble fiber (oats, dal, apple), more omega-3 (walnuts, flaxseeds, fatty fish), regular walking, and less processed food.",
    ],
    key_points: [
      "Oats + dal daily is one of the highest-impact soluble-fiber combos.",
      "Trans fats are far worse than saturated fats.",
      "Get a lipid panel every 2–3 years after age 30.",
    ],
  },
  {
    id: "stress-body",
    title: "Stress — how it silently rewires your body",
    category: "mental",
    reading: "3 min",
    tags: ["cortisol", "anxiety", "burnout"],
    hero: "linear-gradient(135deg,#f472b6 0%,#c026d3 100%)",
    excerpt:
      "Chronic stress isn't just a feeling — it raises cortisol, disrupts sleep, drives cravings, and worsens blood sugar.",
    body: [
      "When stress becomes chronic, your body stays in 'fight-or-flight' mode. Cortisol stays elevated, which pushes blood sugar up, promotes belly-fat storage, disturbs sleep, and weakens immunity.",
      "One of the fastest resets is slow nasal breathing: inhale 4 seconds, hold 4, exhale 6, hold 2. Ten cycles activates the vagus nerve and calms the body.",
      "Longer-term, prioritize sleep, sunlight, a strength/cardio mix 3–4x/week, and talking to someone you trust. Therapy is a superpower — not a last resort.",
    ],
    key_points: [
      "4-4-6-2 breathing calms the nervous system fast.",
      "Chronic stress raises HbA1c and belly-fat.",
      "Therapy is preventive care, not crisis-only.",
    ],
  },
  {
    id: "protein-veg",
    title: "How much protein does a vegetarian Indian need?",
    category: "nutrition",
    reading: "3 min",
    tags: ["protein", "muscle", "dal", "paneer"],
    hero: "linear-gradient(135deg,#34d399 0%,#059669 100%)",
    excerpt:
      "A healthy sedentary adult needs ~0.8 g/kg/day. Active folks 1.2–2.0 g/kg. Most Indian vegetarian meals fall short.",
    body: [
      "A single katori of dal delivers ~6–7 g of protein. One cup of paneer (~100 g) is ~18 g. A boiled egg (for eggetarians) is ~6 g. Sprouts, curd, chana, rajma, tofu and nuts add up.",
      "Rough rule of thumb: dal + curd + one protein-rich side (paneer/soya/tofu/egg) at every major meal. Snack on roasted chana or peanut chikki.",
      "If you're strength training and want to build muscle, aim for 1.6–2.0 g/kg per day and spread it across 3–4 meals.",
    ],
    key_points: [
      "One katori dal is only 6–7 g of protein — you need more sources.",
      "Curd + dal + one protein side per meal covers most people.",
      "Split protein across meals; the body absorbs better in smaller doses.",
    ],
  },
  {
    id: "immunity",
    title: "Immunity — no miracle food, but a strong stack",
    category: "immunity",
    reading: "3 min",
    tags: ["vitamin d", "zinc", "sleep"],
    hero: "linear-gradient(135deg,#84cc16 0%,#4d7c0f 100%)",
    excerpt:
      "There's no immunity 'boost' pill. But sleep, sunlight, movement and a colourful plate stack up massively.",
    body: [
      "Vitamin D deficiency is extremely common in India — despite the sun. Aim for 15–20 minutes of direct morning sunlight and, if deficient, get supplementation from a clinician.",
      "Zinc (nuts, seeds, dal) and vitamin C (amla, guava, lemon, orange) are your everyday allies. Turmeric + black pepper adds curcumin absorption.",
      "Sleep matters more than any supplement. Under 6 hours sleep dramatically reduces infection resistance.",
    ],
    key_points: [
      "Sunlight beats most 'immunity' supplements.",
      "Amla + guava > vitamin C tablets for most people.",
      "Under 6h of sleep drops T-cell response.",
    ],
  },
  {
    id: "womens-cycle",
    title: "Menstrual cycle basics — the 4 phases explained",
    category: "women",
    reading: "3 min",
    tags: ["cycle", "hormones", "pms", "period"],
    hero: "linear-gradient(135deg,#f472b6 0%,#ec4899 100%)",
    excerpt:
      "The average cycle is ~28 days, moving through four hormone phases — each with different energy, mood and food needs.",
    body: [
      "Menstrual (~day 1–5): estrogen and progesterone are lowest. Iron-rich foods (ragi, dates, dal, palak), gentle movement, and rest support this phase.",
      "Follicular (~day 6–13): estrogen rises. Energy is highest — great for strength training, new projects, higher-intensity workouts.",
      "Ovulatory (~day 14): peak of the cycle. Best endurance days.",
      "Luteal (~day 15–28): progesterone rises, sleep can worsen, cravings spike. Magnesium (dark chocolate, almonds, pumpkin seeds) helps.",
      "If cycles are irregular, extremely painful, or come with cystic acne + weight gain, get evaluated for PCOS — it's very common and very manageable early.",
    ],
    key_points: [
      "Track your cycle to plan energy, workouts, and meals.",
      "PCOS is common and improves markedly with lifestyle interventions.",
      "Iron + magnesium are the two most-often-missed nutrients.",
    ],
  },
  {
    id: "bone",
    title: "Bones — the case for calcium AND weight-bearing exercise",
    category: "bone",
    reading: "3 min",
    tags: ["calcium", "vitamin d", "osteoporosis"],
    hero: "linear-gradient(135deg,#e5e7eb 0%,#9ca3af 100%)",
    excerpt:
      "Calcium alone won't save your bones. You need vitamin D to absorb it, and weight-bearing exercise to lock it in.",
    body: [
      "Adults need ~1000 mg of calcium/day (1200 mg after age 50). Dairy (milk, curd, paneer), ragi, sesame (til), almonds and green leafy vegetables are strong sources.",
      "Vitamin D is the key that unlocks calcium absorption. Get sun; supplement if deficient.",
      "Weight-bearing exercise (walking, jogging, dance, resistance training) signals bones to hold density. Swimming is great for joints but does far less for bone density.",
    ],
    key_points: [
      "1000 mg calcium + adequate vitamin D + resistance training.",
      "Ragi is one of the highest calcium grains.",
      "Bone density peaks in the 20s — protect it early.",
    ],
  },
  {
    id: "kids-nutrition",
    title: "What Indian kids should eat — a simple daily template",
    category: "child",
    reading: "3 min",
    tags: ["children", "growth", "school lunch"],
    hero: "linear-gradient(135deg,#fbbf24 0%,#f97316 100%)",
    excerpt:
      "Growth demands protein, calcium and iron. A simple daily template beats memorising nutrient charts.",
    body: [
      "Breakfast: whole grain (dosa, paratha, poha, upma) + protein (egg, curd, milk, moong dal chilla) + fruit.",
      "Lunch: dal + sabzi + roti/rice + curd. Add a boiled egg or paneer for extra protein 3x/week.",
      "Snack: roasted chana, homemade laddoo, fruit, sprouts chaat.",
      "Dinner: keep it light — khichdi, dal chawal, or vegetable soup + roti.",
      "Water throughout the day, and screen-free 30 minutes before bed to protect sleep.",
    ],
    key_points: [
      "Protein at every meal supports growth.",
      "Home-made snacks beat packaged ones.",
      "Sleep is the biggest growth-hormone driver.",
    ],
  },
  {
    id: "seniors",
    title: "Healthy ageing — the four things that matter most",
    category: "senior",
    reading: "3 min",
    tags: ["ageing", "muscle", "balance", "social"],
    hero: "linear-gradient(135deg,#94a3b8 0%,#475569 100%)",
    excerpt:
      "After 60, protein, strength, balance, and social contact matter more than almost anything else you can do.",
    body: [
      "Muscle mass declines ~1% per year after 50. Protein needs actually rise — target 1.0–1.2 g/kg/day, split across meals.",
      "Strength training twice a week (with body weight or light resistance) plus balance work (yoga, tai chi) dramatically reduces fall risk — the single biggest cause of decline after 65.",
      "Loneliness is now considered a health risk on par with smoking. Regular contact with family, friends and community groups is a real medical intervention.",
      "Continue annual check-ups: BP, glucose, lipid panel, eye check, and bone density (DEXA) after 60.",
    ],
    key_points: [
      "1.0–1.2 g/kg protein/day after 60.",
      "Balance training reduces fall risk sharply.",
      "Social contact is a medical intervention.",
    ],
  },
];

export default function KnowledgeLibrary() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    setPageMeta({
      title: "Knowledge Library — Health Questions, Simply Explained | SilentScan AI",
      description:
        "Free, evidence-grounded answers to the health questions India asks most — fatigue, blood pressure, diet, infections, sleep and more. No sign-in needed.",
      path: "/library",
    });
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ARTICLES.filter((a) => {
      const inCat = category === "all" || a.category === category;
      if (!inCat) return false;
      if (!q) return true;
      return (
        a.title.toLowerCase().includes(q) ||
        a.excerpt.toLowerCase().includes(q) ||
        a.tags.some((t) => t.toLowerCase().includes(q))
      );
    });
  }, [query, category]);

  return (
    <div className="min-h-screen bg-[#05050A] text-white pb-24">
      {/* ambient */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(45% 30% at 20% 0%, rgba(52,211,153,0.14) 0%, transparent 60%), radial-gradient(45% 30% at 90% 20%, rgba(167,139,250,0.15) 0%, transparent 60%)",
        }}
      />
      <div className="border-b border-white/5 sticky top-0 z-30 backdrop-blur-md bg-black/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="library-back-home"
            className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            SilentScan<span className="text-cyan-400">.AI</span>
          </Link>
          <Link
            to="/assistant"
            className="text-xs text-zinc-400 hover:text-white px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5"
          >
            Ask the AI
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pt-12">
        <div className="max-w-3xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.35em] text-emerald-300/80">
            Chapter 06 · Knowledge Library
          </div>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl font-semibold leading-[1.02]">
            Everything health.{" "}
            <span className="italic text-cyan-300">Simply explained.</span>
          </h1>
          <p className="mt-5 text-zinc-300 max-w-xl text-lg leading-relaxed">
            Short, evidence-grounded articles on the topics that matter most —
            written for real Indian bodies and kitchens.
          </p>
        </div>

        {/* Search */}
        <div className="mt-8 relative max-w-2xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            data-testid="library-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search topics, tags, questions…"
            className="w-full pl-11 pr-4 py-3.5 rounded-full bg-white/5 border border-white/10 text-white placeholder-zinc-500 focus:outline-none focus:border-cyan-400"
          />
        </div>

        {/* Categories */}
        <div className="mt-6 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              data-testid={`cat-${c.key}`}
              onClick={() => setCategory(c.key)}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs border transition-colors ${
                category === c.key
                  ? "border-cyan-400 bg-cyan-400/10 text-white"
                  : "border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10"
              }`}
            >
              <c.Icon className="w-3.5 h-3.5" />
              {c.label}
            </button>
          ))}
        </div>

        {/* Popular health questions — dedicated SEO pages */}
        <div className="mt-12" data-testid="popular-questions">
          <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-400 font-semibold">
            Popular health questions
          </div>
          <h2 className="mt-2 font-display text-2xl font-semibold">
            The questions India asks most
          </h2>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {ARTICLES_20.map((a) => (
              <Link
                key={a.slug}
                to={`/library/${a.slug}`}
                data-testid={`q-${a.slug}`}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 hover:border-cyan-400/30 hover:bg-white/[0.05] transition-colors"
              >
                <div className="text-[10px] uppercase tracking-widest text-cyan-400/80 font-semibold">
                  {a.category} · {a.reading}
                </div>
                <div className="mt-1 text-sm font-medium text-white leading-snug">
                  {a.question}
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Deep dives */}
        <div className="mt-12 text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">
          Deep dives
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((a) => (
            <ArticleCard
              key={a.id}
              article={a}
              onOpen={() => navigate(`/library/${a.id}`)}
            />
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-center text-sm text-zinc-400">
              No articles match your search yet. Try the AI Assistant for
              open-ended questions.
              <div className="mt-3">
                <Link
                  to="/assistant"
                  className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200"
                >
                  <Sparkles className="w-3.5 h-3.5" /> Ask the AI
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Articles open as dedicated public pages at /library/:slug — no pop-ups */}
    </div>
  );
}

const ArticleCard = ({ article, onOpen }) => (
  <motion.button
    data-testid={`article-${article.id}`}
    onClick={onOpen}
    whileHover={{ y: -4 }}
    transition={{ duration: 0.2 }}
    className="text-left rounded-3xl overflow-hidden border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-colors flex flex-col"
  >
    <div
      className="h-28 w-full"
      style={{ background: article.hero }}
      aria-hidden
    />
    <div className="p-5 flex flex-col flex-1">
      <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
        {article.category} · {article.reading} read
      </div>
      <div className="mt-2 font-display text-lg font-semibold text-white leading-tight">
        {article.title}
      </div>
      <p className="mt-2 text-sm text-zinc-400 leading-relaxed line-clamp-3 flex-1">
        {article.excerpt}
      </p>
      <div className="mt-4 flex items-center justify-between">
        <div className="flex flex-wrap gap-1">
          {article.tags.slice(0, 2).map((t) => (
            <span
              key={t}
              className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-zinc-400"
            >
              #{t}
            </span>
          ))}
        </div>
        <ChevronRight className="w-4 h-4 text-zinc-500" />
      </div>
    </div>
  </motion.button>
);
